// ¿CRECES O SUBISTE LOS PRECIOS? — de dónde salió la diferencia de facturación.
//
// En Argentina, Venezuela y Bolivia ésta es *la* pregunta, y hoy la pantalla no
// la contesta: muestra que facturas más y ahí termina. Pero facturar más porque
// entra más gente y facturar más porque cobras más caro son dos negocios
// distintos, y uno de los dos se está achicando sin que nadie lo vea.
//
// El caso peligroso es `menos autos + más precio`: la facturación sube, el dueño
// está contento, y mientras tanto se le fueron clientes. Ese caso hoy es
// invisible. Este bloque existe básicamente por él.
//
// ── Por qué el "precio" se calcula como facturación / autos ───────────────
//
// La tentación es usar la tarifa real de la lista de precios, o
// `tickets.total.amount / out`. Las dos están mal PARA ESTE BLOQUE, y romperlo
// es fácil si alguien lo "corrige":
//
//     facturación = autos × (facturación / autos)
//
// Es una identidad, no una aproximación. Por eso (1+Δingresos) es exactamente
// (1+Δautos) × (1+Δprecio), y las dos barras explican el 100% del cambio, sin
// resto. Con la tarifa de la lista —o con el monto de tickets solo, dejando
// afuera abonados y convenios— la cuenta no cierra y las barras pasan a ser dos
// números sueltos que no suman lo que el dueño ve arriba.
//
// El costo de esa decisión: "precio por auto" NO es la tarifa. Es lo que deja,
// en promedio, cada auto que pasó. Eso se dice con todas las letras en el
// detalle, porque un dueño que lo lea como "mi tarifa" va a tomar una decisión
// equivocada.
//
// ── Por qué no hay ApexCharts acá ────────────────────────────────────────
//
// Son dos barras. Con dos valores que pueden ser +40% y −1%, la etiqueta de la
// barra chica queda dibujada sobre el eje del cero: texto blanco sobre fondo
// blanco, o texto que se sale del gráfico. Dibujadas a mano, la etiqueta va
// SIEMPRE afuera de la barra, en oscuro sobre el fondo, y se lee igual con
// cualquier par de valores. No se ganaba nada trayendo el gráfico.

import React, { useMemo } from "react";
import Bloque from "./Bloque";
import { ORIGEN, ESTADO, UI } from "./theme";
import { formatCurrency, mesLargo, entero } from "./format";

// El signo se lee por la DIRECCIÓN (a qué lado del cero cae la barra). El color
// es identidad: qué se está midiendo, no si está bien o mal.
const BARRAS = [
  { clave: "autos", rotulo: "Autos", color: ORIGEN.tickets.color },
  { clave: "precio", rotulo: "Precio por auto", color: ORIGEN.otros.color },
];

// Cuántos meses de historia hacen falta para comparar un mes contra el mismo mes
// del año anterior: los 12 de diferencia más el mes en cuestión.
const MESES_NECESARIOS = 13;

// Hasta cuántos meses para atrás se acepta buscar un par comparable. Si el único
// par disponible es de hace ocho meses, eso ya no es "cómo vengo": es historia
// vieja presentada como noticia.
const ANTIGUEDAD_MAX = 3;

/** "2026-08" + n meses. Con Date para no equivocarse en los cruces de año. */
function sumarMeses(ym, n) {
  const [a, m] = String(ym).split("-").map(Number);
  const d = new Date(a, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** "Agosto 2026" — mesLargo devuelve minúscula y acá arranca frase. */
function Mes(ym) {
  const s = mesLargo(ym);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "agosto", sin el año: el año ya está dicho o no aporta. */
function soloMes(ym) {
  return mesLargo(ym).replace(/\s+\d{4}$/, "");
}

/** Variación porcentual. `null` cuando no hay base con qué comparar. */
function variacionPct(actual, previo) {
  const p = Number(previo);
  if (!Number.isFinite(p) || p <= 0) return null;
  return ((Number(actual) - p) / p) * 100;
}

/** "+18%" / "−4%" / "0%". El signo va siempre y el menos es el de verdad (−). */
function pct(v) {
  const r = Math.round(Number(v) || 0);
  if (r === 0) return "0%";
  return `${r > 0 ? "+" : "−"}${Math.abs(r)}%`;
}

/**
 * Días con movimiento del mes.
 *
 * ⚠ `days` del documento mensual NO sirve para esto: es la cantidad de días del
 * calendario (28/30/31), no los días que el estacionamiento operó. Lo verificado
 * en el Desktop (`StatsProcessor._consolidateMonth`) es que
 * `averages.daily_income = income / cantidad de diarios del mes`, así que la
 * división al revés devuelve esa cantidad. Es la única forma de saberlo sin
 * pedirle un campo nuevo al Desktop.
 */
function diasConMovimiento(m) {
  const tope = Number(m?.days) > 0 ? Number(m.days) : 31;
  const ingreso = Number(m?.income);
  const diario = Number(m?.averages?.daily_income);
  if (Number.isFinite(diario) && diario > 0 && Number.isFinite(ingreso) && ingreso > 0) {
    return Math.min(tope, Math.max(1, Math.round(ingreso / diario)));
  }
  const entradas = Number(m?.tickets?.total?.in);
  const diarioT = Number(m?.averages?.daily_tickets_in);
  if (Number.isFinite(diarioT) && diarioT > 0 && Number.isFinite(entradas) && entradas > 0) {
    return Math.min(tope, Math.max(1, Math.round(entradas / diarioT)));
  }
  return null;
}

/**
 * El encabezado: la conclusión en una frase, con los dos números adentro.
 *
 * La tercera oración sólo aparece cuando uno de los dos factores explica el
 * cambio casi entero. Si los dos aportan parecido, decir "es todo precio" sería
 * falso, y este bloque se lee justamente para creerle a esa oración.
 */
function encabezado({ dIngresos, dAutos, dPrecio }) {
  const ri = Math.round(dIngresos);
  const ra = Math.round(dAutos);
  const rp = Math.round(dPrecio);

  const facturas =
    ri > 0 ? `Facturas ${ri}% más que hace un año`
    : ri < 0 ? `Facturas ${Math.abs(ri)}% menos que hace un año`
    : "Facturas casi lo mismo que hace un año";

  const autos =
    ra > 0 ? `con ${ra}% más de autos`
    : ra < 0 ? `con ${Math.abs(ra)}% menos de autos`
    : "con prácticamente los mismos autos";

  let porQue = "";
  // Con menos de 3% de diferencia no hay cambio que explicar, y atribuírselo a
  // algo sería contar un cuento sobre ruido.
  if (Math.abs(ri) >= 3) {
    const crece = ri > 0;
    // "Es todo precio" sólo se puede afirmar si el precio empujó para el mismo
    // lado que la facturación Y los autos no ayudaron: o se movieron en contra,
    // o se movieron tan poco que no explican nada.
    //
    // ⚠ El error fácil acá es pedir sólo que un factor sea el doble del otro.
    // Con +10% de autos y +20% de precio eso da "el crecimiento es todo
    // precio", y es falso: un tercio del crecimiento fue más gente. La
    // afirmación es fuerte, así que la condición también tiene que serlo.
    const domina = (x, otro) =>
      Math.sign(x) === Math.sign(ri) && (Math.sign(otro) !== Math.sign(ri) || Math.abs(x) >= 4 * Math.abs(otro));

    if (domina(rp, ra)) {
      porQue = crece ? " El crecimiento es todo precio." : " La caída es toda de precio.";
    } else if (domina(ra, rp)) {
      porQue = crece ? " El crecimiento es todo por más autos." : " La caída es toda por menos autos.";
    }
  }

  return `${facturas} ${autos}.${porQue}`;
}

/**
 * La frase del cuadrante. Cuatro textos fijos, más los casos en que alguno de
 * los dos ejes no se movió —que no son un cuadrante y no se pueden forzar a uno.
 */
function fraseCrecimiento({ dIngresos, dAutos, dPrecio }) {
  const ri = Math.round(dIngresos);
  const ra = Math.round(dAutos);
  const rp = Math.round(dPrecio);

  if (ra === 0 && rp === 0) return "Estás igual que hace un año: los mismos autos, al mismo precio.";
  if (ra === 0) {
    return rp > 0
      ? "Los mismos autos, a más precio. Todo lo que subiste se lo llevó la tarifa."
      : "Los mismos autos, a menos precio. Revisa la tarifa.";
  }
  if (rp === 0) {
    return ra > 0
      ? "Más autos, al mismo precio. Creciste sin tocar la tarifa."
      : "Menos autos, al mismo precio. Se te fueron clientes.";
  }

  if (ra > 0 && rp > 0) return "Creces de verdad.";
  if (ra > 0 && rp < 0) return "Más autos, más baratos. Revisa la tarifa.";
  if (ra < 0 && rp > 0) {
    // El texto del brief dice "el aumento compensó". Sólo se puede afirmar si la
    // facturación efectivamente subió: con autos −20% y precio +5% no compensó
    // nada, y decirlo sería una mentira tranquilizadora justo en el caso que
    // este bloque existe para destapar.
    return ri >= 0
      ? "El aumento compensó, pero perdiste clientes."
      : "Subiste el precio y aun así facturas menos: perdiste clientes.";
  }
  return "Menos autos y más baratos. Mira esto.";
}

/**
 * Una barra que sale del centro: a la derecha si subió, a la izquierda si bajó.
 *
 * La etiqueta cambia de lugar según el largo de la barra, y no es un capricho:
 * siempre adentro se pierde cuando la barra mide dos píxeles, y siempre afuera
 * se sale de la tarjeta en un teléfono de 320 px cuando la barra es la más
 * larga. Adentro si entra, afuera si no.
 */
const BarraDiferencia = ({ rotulo, valor, color, escala }) => {
  const v = Number(valor) || 0;
  const positivo = v >= 0;
  // 40 y no 50: los 10 puntos que sobran de cada lado son para la etiqueta
  // cuando tiene que salir afuera.
  const ancho = Math.min(40, (Math.abs(v) / escala) * 40);
  const dentro = ancho >= 16;

  const etiqueta = (
    <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", color: dentro ? "#ffffff" : color }}>
      {pct(v)}
    </span>
  );

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11.5, color: UI.textoSuave, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
        {rotulo}
      </div>
      <div style={{ position: "relative", height: 26, background: UI.fondoSuave, borderRadius: 4 }}>
        {/* El cero. Sin esta línea, "a la izquierda" no significa nada. */}
        <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: UI.linea }} />
        <div
          style={{
            position: "absolute",
            top: 3,
            bottom: 3,
            [positivo ? "left" : "right"]: "50%",
            width: `${ancho}%`,
            minWidth: 2,
            background: color,
            borderRadius: positivo ? "0 3px 3px 0" : "3px 0 0 3px",
            display: "flex",
            alignItems: "center",
            justifyContent: positivo ? "flex-end" : "flex-start",
            padding: "0 7px",
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          {dentro && etiqueta}
        </div>
        {!dentro && (
          <div
            style={{
              position: "absolute",
              top: 0,
              lineHeight: "26px",
              [positivo ? "left" : "right"]: `calc(50% + ${ancho}% + 7px)`,
            }}
          >
            {etiqueta}
          </div>
        )}
      </div>
    </div>
  );
};

/** Una fila de la tabla del detalle. */
const Fila = ({ concepto, actual, previo, delta, nota }) => (
  <div
    className="d-flex flex-wrap"
    style={{ padding: "8px 0", borderBottom: `1px solid ${UI.linea}`, gap: 8, alignItems: "baseline" }}
  >
    <div style={{ flex: "1 1 150px", fontSize: 13, color: UI.texto }}>
      {concepto}
      {nota && <div style={{ fontSize: 11.5, color: UI.textoSuave, lineHeight: 1.4 }}>{nota}</div>}
    </div>
    <div style={{ flex: "0 0 120px", fontSize: 13.5, fontWeight: 600, color: UI.texto, textAlign: "right" }}>{actual}</div>
    <div style={{ flex: "0 0 120px", fontSize: 13, color: UI.textoSuave, textAlign: "right" }}>{previo}</div>
    <div style={{ flex: "0 0 70px", fontSize: 13, fontWeight: 600, color: UI.texto, textAlign: "right" }}>{delta}</div>
  </div>
);

const CrecesOSubistePrecios = ({ mensuales = [] }) => {
  const datos = useMemo(() => {
    const ahora = new Date();
    const mesEnCurso = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, "0")}`;

    // El mes en curso queda afuera: la mitad de un mes contra un mes entero
    // siempre da una caída, y sería una caída inventada por el calendario.
    const cerrados = mensuales
      .filter((m) => typeof m?.month === "string" && m.month < mesEnCurso)
      .sort((a, b) => (a.month < b.month ? -1 : 1));

    if (!cerrados.length) return { estado: "sin_historia", meses: 0 };

    const porMes = new Map(cerrados.map((m) => [m.month, m]));

    // Se busca desde el mes cerrado más reciente hacia atrás. Un mes puede
    // faltar —una PC apagada, una sincronización que no corrió— y en ese caso
    // el mes anterior sirve igual, siempre que no sea historia vieja.
    let par = null;
    for (let i = cerrados.length - 1; i >= 0 && i >= cerrados.length - ANTIGUEDAD_MAX; i--) {
      const act = cerrados[i];
      const prev = porMes.get(sumarMeses(act.month, -12));
      if (prev) { par = { act, prev }; break; }
    }

    if (!par) {
      const ultimo = cerrados[cerrados.length - 1];
      if (cerrados.length >= MESES_NECESARIOS) {
        // Hay historia de sobra pero falta justo el mes de la contraparte.
        return { estado: "falta_mes", faltante: sumarMeses(ultimo.month, -12) };
      }
      // Cuándo va a poder: el mes más viejo que hay, más doce.
      return {
        estado: "sin_historia",
        meses: cerrados.length,
        cuando: sumarMeses(cerrados[0].month, 12),
      };
    }

    const { act, prev } = par;
    const ingresoAct = Number(act.income) || 0;
    const ingresoPrev = Number(prev.income) || 0;
    const autosAct = Number(act?.tickets?.total?.out) || 0;
    const autosPrev = Number(prev?.tickets?.total?.out) || 0;

    // Sin autos o sin facturación en alguno de los dos meses no hay división
    // posible, y un "+100%" contra cero no significa nada.
    if (ingresoPrev <= 0 || autosPrev <= 0 || ingresoAct <= 0 || autosAct <= 0) {
      return { estado: "mes_vacio", act: act.month, prev: prev.month };
    }

    const precioAct = ingresoAct / autosAct;
    const precioPrev = ingresoPrev / autosPrev;

    const dIngresos = variacionPct(ingresoAct, ingresoPrev);
    const dAutos = variacionPct(autosAct, autosPrev);
    const dPrecio = variacionPct(precioAct, precioPrev);

    const diasAct = diasConMovimiento(act);
    const diasPrev = diasConMovimiento(prev);

    // Si un mes operó bastante menos días que el otro, parte de la diferencia de
    // autos es eso y no el negocio. No se corrige el número —el dueño factura lo
    // que factura— pero se avisa.
    const desbalance =
      diasAct && diasPrev && Math.abs(diasAct - diasPrev) / Math.max(diasAct, diasPrev) >= 0.1
        ? { diasAct, diasPrev }
        : null;

    return {
      estado: "ok",
      mesAct: act.month, mesPrev: prev.month,
      ingresoAct, ingresoPrev, autosAct, autosPrev, precioAct, precioPrev,
      dIngresos, dAutos, dPrecio,
      diasAct, diasPrev, desbalance,
    };
  }, [mensuales]);

  // ── Estados vacíos ──────────────────────────────────────────────────────
  //
  // Antes de los 13 meses esto se dice con palabras. Mostrar un porcentaje
  // contra "el mes pasado" y llamarlo "contra el año pasado" sería inventar.
  if (datos.estado !== "ok") {
    let frase;
    if (datos.estado === "falta_mes") {
      frase = `En tu historial falta ${mesLargo(datos.faltante)}, que es el mes contra el que habría que comparar.`;
    } else if (datos.estado === "mes_vacio") {
      frase = `${Mes(datos.prev)} figura sin facturación, así que no sirve como punto de partida.`;
    } else if (datos.cuando) {
      frase = `Con ${datos.meses} ${datos.meses === 1 ? "mes" : "meses"} de historia, en ${soloMes(datos.cuando)} sí.`;
    } else {
      frase = "Todavía no cerró ningún mes: el primero se consolida en los primeros días del mes que viene.";
    }

    return (
      <Bloque
        nombre="¿Creces o subiste los precios?"
        id="creces-o-precios"
        titular="Todavía no puedo compararte contra el año pasado"
        frase={frase}
      >
        {/* La explicación del método va SIEMPRE la misma. Antes, en `mes_vacio`
            este párrafo repetía la frase de arriba con otras palabras, y el
            dueño leía dos veces lo mismo sin enterarse de qué hace falta. */}
        <div style={{ fontSize: 12.5, color: UI.textoSuave, lineHeight: 1.5 }}>
          Para separar cuánto de tu facturación es más gente y cuánto es precio más alto hacen
          falta trece meses cerrados: el mes que se mira y el mismo mes del año anterior, los dos
          con autos y facturación. No hay nada que configurar, se consigue con el tiempo.
        </div>
      </Bloque>
    );
  }

  const escala = Math.max(Math.abs(datos.dAutos), Math.abs(datos.dPrecio), 5);

  const valores = { autos: datos.dAutos, precio: datos.dPrecio };

  const detalle = (
    <div>
      <div className="d-flex flex-wrap" style={{ gap: 8, paddingBottom: 6 }}>
        <div style={{ flex: "1 1 150px", fontSize: 11.5, color: UI.textoSuave, textTransform: "uppercase", letterSpacing: 0.4 }}>
          Concepto
        </div>
        <div style={{ flex: "0 0 120px", fontSize: 11.5, color: UI.textoSuave, textAlign: "right" }}>{Mes(datos.mesAct)}</div>
        <div style={{ flex: "0 0 120px", fontSize: 11.5, color: UI.textoSuave, textAlign: "right" }}>{Mes(datos.mesPrev)}</div>
        <div style={{ flex: "0 0 70px", fontSize: 11.5, color: UI.textoSuave, textAlign: "right" }}>Cambio</div>
      </div>

      <Fila
        concepto="Facturación"
        actual={formatCurrency(datos.ingresoAct)}
        previo={formatCurrency(datos.ingresoPrev)}
        delta={pct(datos.dIngresos)}
      />
      <Fila
        concepto="Autos que salieron"
        actual={entero(datos.autosAct)}
        previo={entero(datos.autosPrev)}
        delta={pct(datos.dAutos)}
      />
      <Fila
        concepto="Precio por auto"
        nota="la facturación del mes dividida por los autos que salieron"
        actual={formatCurrency(datos.precioAct)}
        previo={formatCurrency(datos.precioPrev)}
        delta={pct(datos.dPrecio)}
      />

      <div style={{ fontSize: 12.5, color: UI.textoSuave, lineHeight: 1.55, marginTop: 12 }}>
        <p style={{ margin: "0 0 8px" }}>
          <strong style={{ color: UI.texto }}>Precio por auto no es tu tarifa.</strong> Es lo que te
          deja, en promedio, cada auto que pasó, y adentro están los abonados y los convenios. Se
          calcula así porque autos multiplicado por precio por auto da exactamente tu facturación:
          por eso las dos barras explican todo el cambio, sin que quede un resto sin nombre.
        </p>
        <p style={{ margin: "0 0 8px" }}>
          Esta cuenta no descuenta la inflación: compara la plata de {soloMes(datos.mesAct)} de este
          año contra la de hace doce meses, tal como está.
        </p>
        {datos.desbalance && (
          <p style={{ margin: 0 }}>
            {Mes(datos.mesAct)} tuvo {entero(datos.desbalance.diasAct)} días con movimiento y{" "}
            {Mes(datos.mesPrev)} tuvo {entero(datos.desbalance.diasPrev)}. Parte de la diferencia de
            autos es eso, no el negocio.
          </p>
        )}
      </div>
    </div>
  );

  return (
    <Bloque
      nombre="¿Creces o subiste los precios?"
      id="creces-o-precios"
      titular={encabezado(datos)}
      frase={fraseCrecimiento(datos)}
      rango={
        `${Mes(datos.mesAct)} contra ${mesLargo(datos.mesPrev)}` +
        (datos.diasAct && datos.diasPrev
          ? ` · ${entero(datos.diasAct)} y ${entero(datos.diasPrev)} días con movimiento`
          : "")
      }
      // ESTADO, y no POLARIDAD: acá no entra ni sale plata, esto es "algo para
      // mirar". Se marca cuando hay menos autos que hace un año, con o sin
      // aumento: perder clientes es el hallazgo del bloque.
      acento={Math.round(datos.dAutos) < 0 ? ESTADO.alerta.color : null}
      detalle={detalle}
      detalleLabel="Ver los números mes contra mes"
    >
      <div
        role="img"
        aria-label={`Autos ${pct(datos.dAutos)}, precio por auto ${pct(datos.dPrecio)}, contra ${mesLargo(datos.mesPrev)}`}
      >
        {BARRAS.map((b) => (
          <BarraDiferencia
            key={b.clave}
            rotulo={b.rotulo}
            valor={valores[b.clave]}
            color={b.color}
            escala={escala}
          />
        ))}
        <div style={{ fontSize: 11.5, color: UI.textoSuave, lineHeight: 1.45 }}>
          A la derecha de la línea, más que hace un año. A la izquierda, menos.
        </div>
      </div>
    </Bloque>
  );
};

export default CrecesOSubistePrecios;
